/**
 * Regra corporativa de centros de custo (ADR 0092): quais centros cada linha pode usar
 * (elegibilidade) e a divisão de uma operação entre vários centros (rateio).
 *
 * Um motor só: o cálculo é o do FP&A (`allocateMonthWeighted`, maior resto, desempate pelo menor
 * código do centro; ADR 0049, R8). Este arquivo só junta o cálculo à estrutura dos centros. O
 * rateio percentual do Jurídico (`cost-allocation.ts`) é um caso particular (percentual, sem
 * empresa por linha) e continua como está.
 *
 * O pertencimento vem sempre dos vínculos gravados (empresa, departamento, superior direto), nunca
 * do nome. O rateio é gerencial: não gera reembolso, mútuo nem obrigação entre empresas.
 */
import { z } from 'zod';
import { COST_CENTER_LEVELS, type CostCenterLevel } from './cost-center.ts';
import { allocateMonthWeighted, largestRemainder } from './fpa-despesas.ts';
import { fromScaled, toScaled } from './money.ts';

// ---------------------------------------------------------------------------
// Elegibilidade
// ---------------------------------------------------------------------------

/** O que a regra precisa saber de cada centro (lido de `core.cost_center_eligibility()`). */
export interface CostCenterEligibilityNode {
  id: string;
  code: string;
  legalEntityId: string;
  departmentId: string | null;
  parentId: string | null;
  structureLevel: string | null;
  isActive: boolean;
  validFrom: string;
  validUntil: string | null;
}

/** Contexto de uma linha: cada campo vazio não restringe. */
export interface CostCenterEligibilityFilter {
  /** Empresa do centro. */
  legalEntityId?: string | null;
  /** Departamento do centro (o próprio ou, sem departamento, o do superior mais próximo). */
  departmentId?: string | null;
  /** Centro Master ou Gerencial escolhido: só ele e os de baixo dele. */
  ancestorId?: string | null;
  /** Níveis aceitos pela finalidade (vazio = todos). */
  levels?: readonly CostCenterLevel[] | null;
  /** Centros ainda sem nível (legado): aceitos por padrão, na transição. */
  allowUnleveled?: boolean;
  /** Data de referência da vigência (AAAA-MM-DD). Padrão: hoje (UTC). */
  onDate?: string;
  /**
   * Centros já gravados no registro: continuam aceitos mesmo inativos ou fora da vigência
   * (histórico). Nunca muda a empresa, o departamento nem a hierarquia.
   */
  keepIds?: ReadonlySet<string>;
}

export const COST_CENTER_INELIGIBILITY = [
  'not_found',
  'inactive',
  'out_of_validity',
  'other_company',
  'other_department',
  'outside_hierarchy',
  'level_not_allowed',
  'unleveled',
] as const;
export type CostCenterIneligibility = (typeof COST_CENTER_INELIGIBILITY)[number];

type ById = ReadonlyMap<string, CostCenterEligibilityNode>;

const today = () => new Date().toISOString().slice(0, 10);

/** O próprio centro e os superiores, de baixo para cima (para no primeiro ciclo). */
export function costCenterChain(id: string, byId: ById): CostCenterEligibilityNode[] {
  const out: CostCenterEligibilityNode[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id) && out.length < 50) {
    seen.add(cur.id);
    out.push(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return out;
}

/** O Master do centro (ele mesmo, se for Master): null em centro sem nível ou sem Master acima. */
export function costCenterMasterOf<T extends CostCenterEligibilityNode>(
  id: string,
  byId: ReadonlyMap<string, T>,
): T | null {
  const master = costCenterChain(id, byId).find((n) => n.structureLevel === 'master');
  return master ? (byId.get(master.id) ?? null) : null;
}

/** Departamento do centro: o dele ou, sem departamento, o do superior mais próximo. */
export function costCenterDepartmentOf(id: string, byId: ById): string | null {
  return costCenterChain(id, byId).find((n) => n.departmentId)?.departmentId ?? null;
}

/** Por que o centro não serve para a linha (`null` = serve). */
export function costCenterIneligibility(
  id: string,
  filter: CostCenterEligibilityFilter,
  byId: ById,
): CostCenterIneligibility | null {
  const node = byId.get(id);
  if (!node) return 'not_found';
  if (filter.legalEntityId && node.legalEntityId !== filter.legalEntityId) return 'other_company';
  if (filter.departmentId && costCenterDepartmentOf(id, byId) !== filter.departmentId) {
    return 'other_department';
  }
  if (filter.ancestorId && !costCenterChain(id, byId).some((n) => n.id === filter.ancestorId)) {
    return 'outside_hierarchy';
  }
  if (!node.structureLevel) {
    if (filter.allowUnleveled === false) return 'unleveled';
  } else if (filter.levels?.length && !filter.levels.includes(node.structureLevel as never)) {
    return 'level_not_allowed';
  }
  if (filter.keepIds?.has(id)) return null;
  if (!node.isActive) return 'inactive';
  const on = filter.onDate ?? today();
  if (node.validFrom > on || (node.validUntil !== null && node.validUntil < on)) {
    return 'out_of_validity';
  }
  return null;
}

/** Os centros que servem para a linha, em ordem de código. */
export function eligibleCostCenters<T extends CostCenterEligibilityNode>(
  nodes: readonly T[],
  filter: CostCenterEligibilityFilter,
): T[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return nodes
    .filter((n) => costCenterIneligibility(n.id, filter, byId) === null)
    .sort((a, b) => a.code.localeCompare(b.code));
}

// ---------------------------------------------------------------------------
// Rateio
// ---------------------------------------------------------------------------

/**
 * Como a operação é dividida: percentual (soma 100%), quantidade (licenças, pessoas, horas; a
 * proporção vem das quantidades) ou valor fixo por centro (soma igual ao total).
 */
export const COST_SPLIT_METHODS = ['percent', 'quantity', 'amount'] as const;
export type CostSplitMethod = (typeof COST_SPLIT_METHODS)[number];

export const COST_SPLIT_MAX_LINES = 500;

const DECIMAL_RE = /^\d{1,15}(\.\d{1,4})?$/;

export const costSplitLineInput = z.object({
  costCenterId: z.uuid(),
  /**
   * Percentual (até 4 casas), quantidade ou valor da linha, conforme o método. Texto decimal,
   * nunca número de ponto flutuante.
   */
  share: z.string().trim().regex(DECIMAL_RE),
  /** Empresa beneficiada informada pela tela; o servidor confere com a empresa do centro. */
  legalEntityId: z.uuid().nullish(),
});
export type CostSplitLineInput = z.infer<typeof costSplitLineInput>;

export const costSplitInput = z.object({
  method: z.enum(COST_SPLIT_METHODS),
  lines: z.array(costSplitLineInput).min(1).max(COST_SPLIT_MAX_LINES),
  /**
   * Centro da área pagadora, confirmado pela pessoa. Nunca preenchido sozinho: a prévia devolve a
   * sugestão em `suggestedPayerCostCenterId`.
   */
  payerCostCenterId: z.uuid().nullish(),
});
export type CostSplitInput = z.infer<typeof costSplitInput>;

/**
 * Soma das parcelas (texto decimal). Na compra o item ainda não tem preço: no valor fixo, a soma é
 * o total do rateio, e o percentual de cada centro vale depois sobre o preço da proposta.
 */
export function costSplitShareSum(lines: readonly { share: string }[]): string {
  return fromScaled(
    lines.reduce((sum, l) => (DECIMAL_RE.test(l.share) ? sum + toScaled(l.share) : sum), 0n),
  );
}

/** Regras da operação que usa o rateio (cada processo decide as suas). */
export interface CostSplitContext {
  /** Empresa da operação (contratante). */
  legalEntityId: string;
  /** Parcelas em outras empresas do grupo. Sem isso, todas as linhas ficam na empresa da operação. */
  allowCrossCompany: boolean;
  /** Níveis aceitos e centros sem nível, como na elegibilidade. */
  levels?: readonly CostCenterLevel[] | null;
  allowUnleveled?: boolean;
  onDate?: string;
  keepIds?: ReadonlySet<string>;
  /** Valor total da operação (texto decimal). Sem valor, a prévia mostra só os percentuais. */
  total?: string | null;
  /** Casas da moeda (BRL e USD: 2). */
  minorUnits?: number;
}

/** Problema do rateio: caminho (`lines.N.campo` ou `split`) e código para a tela traduzir. */
export interface CostSplitIssue {
  path: string;
  code:
    | CostCenterIneligibility
    | 'duplicate_center'
    | 'invalid_share'
    | 'percent_sum'
    | 'amount_sum'
    | 'total_required'
    | 'cross_company'
    | 'payer_not_in_lines';
  /** Soma encontrada, para a mensagem ("os percentuais somam 90%"). */
  sum?: string;
}

/** Cada parcela: o que Compras, FP&A e Financeiro gravam e herdam. */
export interface CostSplitLine {
  costCenterId: string;
  costCenterCode: string;
  /** Empresa beneficiada: sempre a do centro. */
  legalEntityId: string;
  departmentId: string | null;
  /** Master e Gerencial derivados da hierarquia gravada. */
  masterId: string | null;
  managerialId: string | null;
  /** O que a pessoa informou (percentual, quantidade ou valor). */
  share: string;
  /** Percentual da linha, 4 casas; a soma é exatamente 100. */
  percent: string;
  /** Valor da linha nas casas da moeda; `null` sem valor total. */
  amount: string | null;
}

export interface CostSplitResult {
  ok: boolean;
  issues: CostSplitIssue[];
  lines: CostSplitLine[];
  /** O confirmado (vem da entrada; nunca preenchido sozinho). */
  payerCostCenterId: string | null;
  /** Sugestão: a maior parcela (empate: o menor código). Só sugere; não decide. */
  suggestedPayerCostCenterId: string | null;
  /** Há parcela em empresa diferente da operação. */
  crossCompany: boolean;
  /** Empresas beneficiadas, sem repetir, na ordem das linhas. */
  beneficiaryLegalEntityIds: string[];
}

const HUNDRED = toScaled('100');

/** Master e Gerencial de um centro, só pela hierarquia gravada. */
function placementOf(
  id: string,
  byId: ById,
): { masterId: string | null; managerialId: string | null } {
  const chain = costCenterChain(id, byId);
  return {
    masterId: chain.find((n) => n.structureLevel === 'master')?.id ?? null,
    managerialId: chain.find((n) => n.structureLevel === 'managerial')?.id ?? null,
  };
}

function scaleOf(value: string): number {
  return value.split('.')[1]?.length ?? 0;
}

/**
 * Confere e calcula o rateio. Mesma função na tela (prévia) e no servidor (gravação): a tela só
 * ajuda; o servidor recusa o que não passar.
 */
export function computeCostSplit(
  input: CostSplitInput,
  nodes: readonly CostCenterEligibilityNode[],
  ctx: CostSplitContext,
): CostSplitResult {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const minor = ctx.minorUnits ?? 2;
  const issues: CostSplitIssue[] = [];
  const seen = new Set<string>();
  const filter: CostCenterEligibilityFilter = {
    levels: ctx.levels,
    allowUnleveled: ctx.allowUnleveled,
    onDate: ctx.onDate,
    keepIds: ctx.keepIds,
  };

  input.lines.forEach((l, i) => {
    const path = `lines.${i}`;
    const node = byId.get(l.costCenterId);
    const why = costCenterIneligibility(l.costCenterId, filter, byId);
    if (why) issues.push({ path: `${path}.costCenterId`, code: why });
    if (node && l.legalEntityId && l.legalEntityId !== node.legalEntityId) {
      issues.push({ path: `${path}.legalEntityId`, code: 'other_company' });
    }
    if (node && !ctx.allowCrossCompany && node.legalEntityId !== ctx.legalEntityId) {
      issues.push({ path: `${path}.costCenterId`, code: 'cross_company' });
    }
    if (seen.has(l.costCenterId)) {
      issues.push({ path: `${path}.costCenterId`, code: 'duplicate_center' });
    }
    seen.add(l.costCenterId);
    const validShare =
      DECIMAL_RE.test(l.share) &&
      toScaled(l.share) > 0n &&
      (input.method !== 'percent' || toScaled(l.share) <= HUNDRED) &&
      (input.method !== 'amount' || scaleOf(l.share) <= minor);
    if (!validShare) issues.push({ path: `${path}.share`, code: 'invalid_share' });
  });

  const sharesOk = !issues.some((x) => x.code === 'invalid_share');
  const shareSum = sharesOk ? input.lines.reduce((s, l) => s + toScaled(l.share), 0n) : 0n;
  const total = ctx.total?.trim() ? ctx.total.trim() : null;
  if (sharesOk && input.method === 'percent' && shareSum !== HUNDRED) {
    issues.push({ path: 'split', code: 'percent_sum', sum: fromScaled(shareSum) });
  }
  if (input.method === 'amount') {
    if (!total) issues.push({ path: 'split', code: 'total_required' });
    else if (sharesOk && shareSum !== toScaled(total)) {
      issues.push({ path: 'split', code: 'amount_sum', sum: fromScaled(shareSum) });
    }
  }
  if (input.payerCostCenterId && !seen.has(input.payerCostCenterId)) {
    issues.push({ path: 'payerCostCenterId', code: 'payer_not_in_lines' });
  }

  const ok = issues.length === 0;
  const code = (id: string) => byId.get(id)?.code ?? id;
  // Percentuais exibidos: no percentual, o informado; nos demais, a proporção, somando 100.
  const weights = input.lines.map((l) => ({
    weight: sharesOk ? toScaled(l.share) : 0n,
    tieKey: code(l.costCenterId),
  }));
  const percents =
    !sharesOk || shareSum === 0n
      ? input.lines.map(() => null)
      : input.method === 'percent'
        ? input.lines.map((l) => fromScaled(toScaled(l.share)))
        : largestRemainder(HUNDRED, 4, weights).map((p) => fromScaled(p));
  let amounts: (string | null)[] = input.lines.map(() => null);
  if (ok && total) {
    amounts =
      input.method === 'amount'
        ? input.lines.map((l) => fromScaled(toScaled(l.share)))
        : allocateMonthWeighted(
            total,
            minor,
            input.lines.map((l, i) => ({
              key: String(i),
              costCenterCode: code(l.costCenterId),
              weight: l.share,
            })),
          ).map((p) => p.amount);
  }

  const lines: CostSplitLine[] = input.lines.map((l, i) => {
    const node = byId.get(l.costCenterId);
    return {
      costCenterId: l.costCenterId,
      costCenterCode: node?.code ?? '',
      legalEntityId: node?.legalEntityId ?? l.legalEntityId ?? '',
      departmentId: node ? costCenterDepartmentOf(node.id, byId) : null,
      ...(node ? placementOf(node.id, byId) : { masterId: null, managerialId: null }),
      share: l.share,
      percent: percents[i] ?? '',
      amount: amounts[i] ?? null,
    };
  });

  // Sugestão de área pagadora: a maior parcela pelo peso (empate: o menor código).
  let best = -1;
  if (sharesOk) {
    lines.forEach((l, i) => {
      const w = weights[i]!.weight;
      const b = best < 0 ? -1n : weights[best]!.weight;
      if (w > b || (w === b && l.costCenterCode < lines[best]!.costCenterCode)) best = i;
    });
  }
  const companies = [...new Set(lines.map((l) => l.legalEntityId).filter(Boolean))];

  return {
    ok,
    issues,
    lines,
    payerCostCenterId: input.payerCostCenterId ?? null,
    suggestedPayerCostCenterId: best < 0 ? null : lines[best]!.costCenterId,
    crossCompany: lines.some((l) => l.legalEntityId && l.legalEntityId !== ctx.legalEntityId),
    beneficiaryLegalEntityIds: companies,
  };
}

/**
 * Quem responde pela aprovação da necessidade em cada centro, uma vez por pessoa, na ordem das
 * linhas. Só a lista: quem aprova e em que ordem é regra da frente de Alçadas. Centros sem
 * aprovador ficam em `missing`.
 */
export function costSplitApprovers(
  lines: readonly { costCenterId: string }[],
  approverOf: (costCenterId: string) => string | null | undefined,
): { userIds: string[]; missing: string[] } {
  const userIds: string[] = [];
  const missing: string[] = [];
  for (const l of lines) {
    const u = approverOf(l.costCenterId);
    if (!u) missing.push(l.costCenterId);
    else if (!userIds.includes(u)) userIds.push(u);
  }
  return { userIds, missing };
}

/** Linhas iguais: o mesmo percentual em todas, o resto no menor código (soma exatamente 100). */
export function evenPercents(codes: readonly string[]): string[] {
  if (!codes.length) return [];
  return largestRemainder(
    HUNDRED,
    2,
    codes.map((c) => ({ weight: 1n, tieKey: c })),
  ).map((p) => fromScaled(p));
}

/** O que falta para 100% (ou para o total, no valor fixo), para completar a última linha. */
export function remainderForLast(
  method: CostSplitMethod,
  shares: readonly string[],
  total?: string | null,
): string | null {
  if (method === 'quantity' || shares.length === 0) return null;
  const target = method === 'percent' ? HUNDRED : total ? toScaled(total) : null;
  if (target === null) return null;
  const others = shares.slice(0, -1);
  if (others.some((s) => !DECIMAL_RE.test(s))) return null;
  const rest = target - others.reduce((s, x) => s + toScaled(x), 0n);
  return rest > 0n ? fromScaled(rest) : null;
}

/** Pedido de prévia (`POST /core/cost-centers/split/preview`): o rateio e as regras do processo. */
export const costSplitPreviewInput = z.object({
  split: costSplitInput,
  legalEntityId: z.uuid(),
  allowCrossCompany: z.boolean().default(false),
  levels: z.array(z.enum(COST_CENTER_LEVELS)).nullish(),
  allowUnleveled: z.boolean().optional(),
  onDate: z.iso.date().optional(),
  keepIds: z.array(z.uuid()).max(COST_SPLIT_MAX_LINES).optional(),
  total: z.string().trim().regex(DECIMAL_RE).nullish(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullish(),
});
export type CostSplitPreviewInput = z.infer<typeof costSplitPreviewInput>;
